import { Unstable_Grid2 as Grid, TextField } from '@mui/material';
import React, { useState } from 'react';

import {
  AdditionalButtonProps,
  Button,
  CircularProgress,
  ConfirmDialog,
  Typography,
} from '@occc-app/design-system';

type Props = {
  helpText?: string;
  label: string;
  loading?: boolean;
  onAdd: (value: string) => any;
  onDelete?: (value: string) => any;
  deleteConfirmDialog?: boolean;
  deleteConfirmDialogTitle?: string;
  isError?: boolean;
  onBlur?: () => void;
  onInputChange?: () => void;
  required?: boolean;
  parse?: (value: string) => any;
  additionalButton?: React.ComponentType<AdditionalButtonProps>;
};

export const FormInputWithButtonRow: React.FC<Props> = ({
  helpText,
  isError,
  onBlur,
  onInputChange,
  required,
  onAdd,
  label,
  loading,
  onDelete,
  deleteConfirmDialog,
  deleteConfirmDialogTitle,
  parse,
  additionalButton: AdditionalButton,
}) => {
  const [value, setValue] = useState<string>('');
  const [openRemoveDialog, setOpenRemoveDialog] = useState<boolean>(false);
  if (loading !== undefined && loading) {
    return <CircularProgress size={24} />;
  }

  const handleAddValue = () => {
    if (!value.length) {
      return;
    }

    const valueToAdd = parse ? parse(value) : value;
    const stringValueToAdd = valueToAdd == null ? '' : String(valueToAdd);

    if (stringValueToAdd.length > 0) {
      onAdd(stringValueToAdd);
    }

    // Removes raw value when parse returns empty
    // because raw value shouldn't be in the input in a first place
    setValue('');
  };

  const handleDeleteValue = () => {
    if (onDelete && value.length > 0) {
      onDelete(value);
      setValue('');
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.currentTarget.value;
    const parsedValue = parse ? parse(rawValue) : rawValue;

    setValue(parsedValue ?? '');
    onInputChange?.();
  };

  const handleOnSubmit = (event: React.KeyboardEvent) => {
    if (event.keyCode === 13 && !onDelete) {
      handleAddValue();
    }
  };

  const onDeleteClick = () => {
    if (deleteConfirmDialog && value.length > 0) {
      setOpenRemoveDialog(true);
    } else {
      handleDeleteValue();
    }
  };

  const onDeleteConfirmClick = () => {
    handleDeleteValue();
    setOpenRemoveDialog(false);
  };

  return (
    <Grid container spacing={2} direction={'row'} flexWrap={'wrap'} alignItems={'center'}>
      <Grid xs={12} md={6}>
        <TextField
          label={`${label}`}
          helperText={helpText}
          onChange={handleOnChange}
          onKeyDown={handleOnSubmit}
          value={value}
          error={isError}
          onBlur={onBlur}
          required={required}
          fullWidth={true}
        />
      </Grid>

      <Grid xs={6} sm={'auto'}>
        <Button variant='contained' color='primary' onClick={handleAddValue}>
          +Add
        </Button>
      </Grid>

      {onDelete && (
        <Grid xs={6} md={'auto'}>
          <Button variant='contained' color='error' onClick={onDeleteClick}>
            Remove
          </Button>
        </Grid>
      )}

      {AdditionalButton && (
        <Grid xs={12} md={4}>
          <AdditionalButton value={value} handleAddValue={handleAddValue} />
        </Grid>
      )}

      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={openRemoveDialog}
        onClose={() => setOpenRemoveDialog(false)}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onDeleteConfirmClick,
          color: 'error',
        }}
      >
        {deleteConfirmDialogTitle && <Typography>{deleteConfirmDialogTitle}</Typography>}
      </ConfirmDialog>
    </Grid>
  );
};
